import { useEffect, useState } from "react";
import { RevisionChip } from "./revision-chip";
import { AnimatePresence, motion } from "motion/react";
import { Check, CircleCheck, Sparkles, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrettyDate, localDateString } from "@/lib/app-types";
import { MAX_LECTURES, chapterStats, type StudyChapter } from "@/lib/lecture-plan";
import { BottomSheet } from "./bottom-sheet";
import { RippleButton } from "./ripple-button";
import { DateField } from "./date-field";

export function ChapterDetailSheet({
  chapter,
  today,
  busy,
  onClose,
  onMarkLecture,
  onCompleteRevisions,
  onUpdateLectures,
  onDelete,
}: {
  chapter: StudyChapter | null;
  today: string;
  busy?: boolean;
  onClose: () => void;
  onMarkLecture: (lectureId: string, completedOn: string | null) => void;
  onCompleteRevisions: (ids: string[]) => void;
  onUpdateLectures: (total: number) => void;
  onDelete: () => void;
}) {
  const [date, setDate] = useState(localDateString());
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (chapter) {
      setDate(localDateString());
      setEditing(false);
    }
  }, [chapter?.id]);

  const stats = chapter ? chapterStats(chapter, today) : null;
  const due = chapter
    ? chapter.revisions
        .filter((r) => r.status === "pending" && r.dueDate <= today)
        .sort((a, b) => a.lectureNumber - b.lectureNumber)
    : [];

  return (
    <BottomSheet
      open={chapter !== null}
      tall
      onClose={onClose}
      title={chapter?.chapter ?? ""}
      subtitle={chapter?.subject}
    >
      {chapter && stats ? (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { label: "Completed", value: `${stats.completed}` },
              { label: "Remaining", value: `${stats.remaining}` },
              { label: "Progress", value: `${stats.percent}%` },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-hairline bg-surface py-3">
                <p className="text-lg font-extrabold tabular-nums">{item.value}</p>
                <p className="text-[11px] text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-hairline bg-surface px-4 py-3 text-[12px]">
            <span className="text-muted-foreground">
              Last revision
              <span className="ml-1.5 font-semibold text-foreground">
                {stats.lastRevision ? formatPrettyDate(stats.lastRevision) : "—"}
              </span>
            </span>
            <span className="text-muted-foreground">
              Next
              <span className="ml-1.5 font-semibold text-foreground">
                {stats.nextRevision ? formatPrettyDate(stats.nextRevision) : "—"}
              </span>
            </span>
          </div>

          <AnimatePresence initial={false}>
            {due.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="rounded-[22px] border border-primary/25 bg-primary/8 p-4"
              >
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  <Sparkles className="size-3.5" /> Revision due · {due.length} lectures
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {due.map((revision) => (
                    <RevisionChip
                      key={revision.id}
                      revision={revision}
                      today={today}
                      disabled={busy}
                      onComplete={(id) => onCompleteRevisions([id])}
                    />
                  ))}
                </div>
                <RippleButton
                  size="sm"
                  className="mt-4 w-full"
                  disabled={busy}
                  onClick={() => onCompleteRevisions(due.map((r) => r.id))}
                >
                  <CircleCheck className="size-4" /> Mark all revised
                </RippleButton>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <DateField value={date} onChange={setDate} label="Mark lectures completed on" />

          <div>
            <div className="flex items-baseline justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Lectures
              </p>
              <button
                type="button"
                onClick={() => setEditing((v) => !v)}
                className="text-[11px] font-semibold text-primary"
              >
                {editing ? "Done" : "Change count"}
              </button>
            </div>

            {editing ? (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2.5 grid grid-cols-10 gap-1.5"
              >
                {Array.from({ length: MAX_LECTURES }, (_, i) => i + 1).map((n) => (
                  <motion.button
                    key={n}
                    type="button"
                    whileTap={{ scale: 0.88 }}
                    onClick={() => onUpdateLectures(n)}
                    className={cn(
                      "rounded-xl py-2 text-[11px] font-bold tabular-nums",
                      n === chapter.totalLectures
                        ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                        : "border border-hairline bg-surface text-muted-foreground",
                    )}
                  >
                    {n}
                  </motion.button>
                ))}
              </motion.div>
            ) : (
              <div className="mt-2.5 grid grid-cols-4 gap-2">
                {chapter.lectures.map((lecture) => {
                  const done = Boolean(lecture.completedOn);
                  return (
                    <motion.button
                      key={lecture.id}
                      type="button"
                      whileTap={{ scale: 0.92 }}
                      disabled={busy}
                      onClick={() => onMarkLecture(lecture.id, done ? null : date)}
                      className={cn(
                        "relative flex flex-col items-start gap-1 rounded-2xl px-3 py-2.5 text-left transition-colors",
                        done
                          ? "border border-primary/30 bg-primary/12"
                          : "border border-hairline bg-surface",
                      )}
                    >
                      <span className="flex w-full items-center justify-between">
                        <span className="text-[12px] font-bold">L{lecture.number}</span>
                        {done ? <Check className="size-3.5 text-primary" /> : null}
                      </span>
                      <span className="text-[9px] text-muted-foreground">
                        {lecture.completedOn
                          ? new Date(`${lecture.completedOn}T00:00:00`).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                            })
                          : "Pending"}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-destructive/25 bg-destructive/10 py-3 text-[13px] font-semibold text-destructive"
          >
            <Trash2 className="size-4" /> Delete chapter
          </button>
        </div>
      ) : null}
    </BottomSheet>
  );
}